<script lang="ts" setup>
import { Level, Sex } from '~/stores/game';
import type { CategoryGroupType, TaskType } from '~/types/tasks';

const props = withDefaults(
  defineProps<{
    task?: TaskType | null;
    isNotFound?: boolean;
    categories?: CategoryGroupType[] | null;
    withTags?: boolean;
    mediaPreview?: boolean;
  }>(),
  { mediaPreview: true },
);

const { t } = useI18n();

// TODO(game_level_and_sex): дублирование что не очень хорошо
const stringLevel = computed(
  () =>
    ({
      [Level.easy]: t('variables.levels.0'),
      [Level.average]: t('variables.levels.1'),
      [Level.hot]: t('variables.levels.2'),
    })[(props.task?.level as Level) || Level.easy],
);
const stringSex = computed(
  () =>
    ({
      [Sex.girl]: t('variables.target.girl').toLowerCase(),
      [Sex.boy]: t('variables.target.boy').toLowerCase(),
    })[(props.task?.target || Sex.boy) as Sex],
);

const taskCategories = computed(() =>
  props.withTags && props.categories && props.task?.categoryIds
    ? props.task.categoryIds.map((el) =>
        props.categories!.reduce((acc, g) => {
          const category = g.categories.find((c) => c.id === el);
          if (category) return t(`categories.groups.${g.id}.items.${category.id}`);
          return acc;
        }, ''),
      )
    : [],
);
</script>

<template>
  <Card
    class="gameSimpleCard"
    :pt="{
      body: 'h-full',
      content: 'h-full flex flex-col',
    }"
    data-test-id="game-card"
  >
    <template #content>
      <div class="flex gap-2 mb-2">
        <h1 v-if="!isNotFound" class="m-0 text-2xl">
          {{ t('task_level', { level: stringLevel }) }}
        </h1>

        <slot name="head" />
      </div>
      <h2 class="text-xl mt-0" v-if="!isNotFound">
        {{ t('task_sex', { sex: stringSex }) }}
      </h2>

      <p
        v-if="!isNotFound && task"
        class="leading-6 select-none mt-0"
        data-test-id="game-card-content"
        :data-task-id="task.id"
        :data-level="task.level"
      >
        {{ task.content || t('media_only_content') }}
      </p>
      <p v-else class="text-lg mt-0">
        {{ t('task_empty') }}
        <!-- TODO: <Button label="Добавить разнообразия" /> -->
      </p>
      <div class="flex flex-wrap gap-2 mt-auto" v-if="withTags">
        <Tag v-if="task?.settings?.public" value="Общедоступное" class="p-tag-success" />
        <Tag v-if="task?.settings?.shared" value="Доступно партнеру" class="p-tag-success" />

        <Tag v-for="category in taskCategories" :key="category" :value="category" class="p-tag-info" />
      </div>

      <div class="gameSimpleCard__additional">
        <GameMedia
          v-if="task?.media && !isNotFound"
          :key="task.media"
          :url="task.media"
          :alt="task.content || t('media_only_content')"
          :preview="mediaPreview"
          class="gameSimpleCard__image"
        />

        <slot name="timer" />
      </div>

      <slot name="additional" />
    </template>
  </Card>
</template>

<i18n src="#locales/categories.yaml"></i18n>
<i18n src="~/locales/global.yaml"></i18n>
<i18n>
en:
  task_level: '{level} level'
  task_sex: Task for {sex}
  task_empty: At the current level, the tasks are over. Let's level up the game :)
  media_only_content: Repeat the steps as in the image below.

ru:
  task_level: '{level} уровень'
  task_sex: Задание для {sex}
  task_empty: На текущем уровне задания закончились. Предлагаем повысить уровень игры :)
  media_only_content: Повторите действия как на изображении ниже.

de:
  task_level: '{level} Ebene'
  task_sex: Aufgabe für {sex}
  task_empty: Auf dem aktuellen Stand sind die Aufgaben erledigt. Wir empfehlen Ihnen, Ihr Spielniveau zu erhöhen :)
  media_only_content: Wiederholen Sie die Schritte wie im Bild unten.

es:
  task_level: '{level} nivel'
  task_sex: Tarea para {sex}
  task_empty: Al nivel actual, las tareas han terminado. Te sugerimos que subas tu nivel de juego :)
  media_only_content: Repita los pasos como en la imagen de abajo.

fr:
  task_level: Niveau {level}
  task_sex: Mission pour {sex}
  task_empty: Au niveau actuel, les tâches sont terminées. Nous vous suggérons d'élever votre niveau de jeu :)
  media_only_content: Répétez les étapes comme dans l'image ci-dessous.
</i18n>

<style lang="scss">
.gameSimpleCard {
  z-index: 2;
  position: relative;
  width: 100%;
  border-radius: 24px !important;
  max-width: 720px;
  margin: 0 auto;

  &__additional {
    display: flex;
    align-items: center;
    flex-direction: column;
    padding-top: 16px;

    &:empty {
      display: none;
    }
  }

  &__image {
    max-width: 100%;

    img {
      max-width: 100%;
    }
  }
}
</style>
